import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { Button } from '@/ui/Button/Button';
import { Flex } from '@/ui/Flex/Flex';
import { Modal } from '@/ui/Modal/Modal';
import { T } from '@/ui/Text/Text';
import { useNavigate } from 'react-router-dom';
import Switch from 'react-switch';
import { envIsElectron } from 'src/const/envCheck';

interface Props {
  isModalOpen: boolean;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  currentTags: { tag: string; channel: string }[];
  connectionStatus: boolean;
}

const StyledSaveButton = styled(Button)`
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  align-self: flex-end;
  cursor: pointer;

  ${({ disabled }) =>
    disabled &&
    css`
      background-color: ${({ theme }) => theme.color('#B2B1BA')};
      pointer-events: none;
    `}
`;

const StyledSwitchGroup = styled.div<{ readonly enabled: boolean }>`
  display: flex;
  gap: 8px;
  align-items: center;
  cursor: pointer;

  .switch {
    ${({ enabled, theme }) =>
      enabled &&
      css`
        .react-switch-bg {
          background: ${theme.color('#158AD6')} !important;
        }
      `}
  }
`;

export const PlayerInfoModal = ({
  isModalOpen,
  setIsModalOpen,
  currentTags,
  connectionStatus,
}: Props) => {
  const navigate = useNavigate();
  const [isFullscreen, setIsFullscreen] = useState(true);

  const exitPresentation = () => {
    if (envIsElectron && isFullscreen) {
      window.electron.ipcRenderer.sendMessage('shutdownPlayer');
    }
    navigate(-1);
  };
  const getCurrentTagByChannel = (channel: string) => {
    const foundTag = currentTags.find((tag) => {
      return tag.channel === channel.toString();
    });

    if (foundTag) return foundTag?.tag;
    return null;
  };

  const [channelTags, setChannelTags] = useState({
    tagOne: '',
    tagTwo: '',
    tagThree: '',
    tagFour: '',
    mocked: '',
  });
  useEffect(() => {
    const fetchFullscreenStatus = async () => {
      const status = await window.electron.getFullscreenStatus();
      setIsFullscreen(!!status);
    };

    fetchFullscreenStatus();
  }, [isModalOpen]);
  useEffect(() => {
    const tagOne = getCurrentTagByChannel('1');
    const tagTwo = getCurrentTagByChannel('2');
    const tagThree = getCurrentTagByChannel('3');
    const tagFour = getCurrentTagByChannel('4');
    const mocked = getCurrentTagByChannel('mocked');

    setChannelTags({
      tagOne: tagOne || '',
      tagTwo: tagTwo || '',
      tagThree: tagThree || '',
      tagFour: tagFour || '',
      mocked: mocked || '',
    });
  }, [currentTags]);

  return isModalOpen ? (
    <Modal
      onClose={() => setIsModalOpen(false)}
      title="Menu"
      footer={
        <div style={{ width: '100%' }}>
          <Flex
            padding={24}
            justify={envIsElectron ? 'space-between' : 'flex-end'}
            direction="row"
            align="center"
          >
            {envIsElectron && (
              <StyledSwitchGroup
                enabled={isFullscreen}
                onClick={() => {
                  window.electron.ipcRenderer.sendMessage('toggleFullscreen');
                  setIsFullscreen(!isFullscreen);
                }}
              >
                <T p>
                  <FormattedMessage defaultMessage="Fullscreen" id="zvKOAu" />:
                </T>
                <Switch
                  className="switch"
                  checked={isFullscreen}
                  onChange={() => {}}
                />
              </StyledSwitchGroup>
            )}
            <Flex gap={24}>
              <StyledSaveButton onClick={() => setIsModalOpen(false)}>
                <FormattedMessage defaultMessage="Cancel" id="47FYwb" />
              </StyledSaveButton>

              <StyledSaveButton mode="danger" onClick={exitPresentation}>
                <FormattedMessage
                  defaultMessage="Exit Presentation"
                  id="k94WLg"
                />
              </StyledSaveButton>
            </Flex>
          </Flex>
        </div>
      }
    >
      <Flex gap={16} direction="column">
        <Flex gap={16} direction="column">
          <T h4>Trihow Link Status:</T>
          {connectionStatus ? (
            <FormattedMessage defaultMessage="Connected" id="IvjoDS" />
          ) : (
            <FormattedMessage defaultMessage="Disconnected" id="FZeQlc" />
          )}
        </Flex>
        <Flex direction="column">
          <Flex gap={8} direction="row">
            <T h5>Reader 1:</T>
            <strong>
              {channelTags.tagOne ? (
                <T p>{channelTags.tagOne}</T>
              ) : (
                <T p>
                  <FormattedMessage defaultMessage="No tag ID" id="4ibWn/" />
                </T>
              )}
            </strong>
          </Flex>

          <Flex gap={8} direction="row">
            <T h5>Reader 2:</T>
            <strong>
              {channelTags.tagTwo ? (
                <T p>{channelTags.tagTwo}</T>
              ) : (
                <T p>
                  <FormattedMessage defaultMessage="No tag ID" id="4ibWn/" />
                </T>
              )}
            </strong>
          </Flex>
          <Flex gap={8} direction="row">
            <T h5>Reader 3:</T>
            <strong>
              {channelTags.tagThree ? (
                <T p>{channelTags.tagThree}</T>
              ) : (
                <T p>
                  <FormattedMessage defaultMessage="No tag ID" id="4ibWn/" />
                </T>
              )}
            </strong>
          </Flex>
          <Flex gap={8} direction="row">
            <T h5>Reader 4:</T>
            <strong>
              {channelTags.tagFour ? (
                <T p>{channelTags.tagFour}</T>
              ) : (
                <T p>
                  <FormattedMessage defaultMessage="No tag ID" id="4ibWn/" />
                </T>
              )}
            </strong>
          </Flex>
          {process.env.NODE_ENV === 'development' && (
            <Flex gap={16} direction="row">
              <T h5>Reader Mocked (Development):</T>
              <strong>
                {channelTags.mocked ? (
                  <T p>{channelTags.mocked}</T>
                ) : (
                  <T p>
                    <FormattedMessage defaultMessage="No tag ID" id="4ibWn/" />
                  </T>
                )}
              </strong>
            </Flex>
          )}
        </Flex>
      </Flex>
    </Modal>
  ) : null;
};
